/**
 * API Endpoints
 * Complete paths including /api prefix — use directly with httpClient
 */

export const SERVICE_PATHS = {
  USM: '/api/usm/v1',
  CONNECTOR: '/api/con/v1',
  WORKFLOW: '/api/wfw',
  TRIGGER: '/api/trg/v1',
  ORCHESTRATION: '/api/orch',
  AUDIT: '/api/aud/v1',
} as const


export const ENDPOINTS = {
  // Gateway Auth Endpoints
  GATEWAY: {
    LOGIN: '/auth/login',
    CALLBACK: '/auth/callback',
    ME: `${SERVICE_PATHS.USM}/users/me`,
    LOGOUT: '/auth/logout',
    HEALTH: '/health',
  },

  // User Management Service — Admin Endpoints
  USM: {
    ADMIN: {
      PLATFORM: {
        STATS: `${SERVICE_PATHS.USM}/admin/platform/stats`,
        HEALTH: `${SERVICE_PATHS.USM}/admin/platform/health`,
      },
      TENANTS: {
        LIST: `${SERVICE_PATHS.USM}/admin/tenants`,
        BY_ID: (id: string) => `${SERVICE_PATHS.USM}/admin/tenants/${id}`,
        ACTIVE: `${SERVICE_PATHS.USM}/admin/tenants/active`,
        CREATE_SIMPLE: `${SERVICE_PATHS.USM}/admin/tenants/simple`,
        ACTIVATE: (id: string) => `${SERVICE_PATHS.USM}/admin/tenants/${id}/activate`,
        SUSPEND: (id: string) => `${SERVICE_PATHS.USM}/admin/tenants/${id}/suspend`,
        DEACTIVATE: (id: string) => `${SERVICE_PATHS.USM}/admin/tenants/${id}/deactivate`,
        SOFT_DELETE: (id: string) => `${SERVICE_PATHS.USM}/admin/tenants/${id}/delete`,
        RESTORE: (id: string) => `${SERVICE_PATHS.USM}/admin/tenants/${id}/restore`,
        USERS: (id: string) => `${SERVICE_PATHS.USM}/admin/tenants/${id}/users`,
      },
      USERS: {
        LIST: `${SERVICE_PATHS.USM}/admin/users`,
        BY_ID: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}`,
        ACTIVATE: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}/activate`,
        DEACTIVATE: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}/deactivate`,
        PROMOTE_SUPERADMIN: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}/promote-superadmin`,
        DEMOTE_SUPERADMIN: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}/demote-superadmin`,
        PROMOTE_DEVADMIN: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}/promote-devadmin`,
        DEMOTE_DEVADMIN: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}/demote-devadmin`,
        ASSIGN_TENANT: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}/assign-tenant`,
        DELETE: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}`,
        SET_ROLE: (id: string) => `${SERVICE_PATHS.USM}/admin/users/${id}/role`,
      },
      TEAMS: `${SERVICE_PATHS.USM}/admin/teams`,
      PRESENTATION: {
        CATALOG: `${SERVICE_PATHS.USM}/admin/presentation-catalog`,
        PRESETS: `${SERVICE_PATHS.USM}/admin/presentation-presets`,
        PRESET_BY_KEY: (key: string) => `${SERVICE_PATHS.USM}/admin/presentation-presets/${key}`,
        PRESET_DUPLICATE: (key: string) => `${SERVICE_PATHS.USM}/admin/presentation-presets/${key}/duplicate`,
        TENANT: (tenantId: string) => `${SERVICE_PATHS.USM}/admin/tenants/${tenantId}/presentation`,
      },
    },
    TEAMS: {
      LIST: `${SERVICE_PATHS.USM}/teams`,
      BY_ID: (id: string) => `${SERVICE_PATHS.USM}/teams/${id}`,
      MEMBERS: (id: string) => `${SERVICE_PATHS.USM}/teams/${id}/members`,
    },
    INVITATIONS: {
      LIST: `${SERVICE_PATHS.USM}/invitations`,
      ALL: `${SERVICE_PATHS.USM}/invitations/all`,
      BY_ID: (id: string) => `${SERVICE_PATHS.USM}/invitations/${id}`,
      VALIDATE: (token: string) => `${SERVICE_PATHS.USM}/invitations/validate/${token}`,
      RESEND: (id: string) => `${SERVICE_PATHS.USM}/invitations/${id}/resend`,
    },
  },

  // Connector Service
  CONNECTOR: {
    CONNECTIONS: `${SERVICE_PATHS.CONNECTOR}/connections`,
    CONNECTION_BY_ID: (id: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${id}`,
    CONNECTIONS_AUTHORIZE: `${SERVICE_PATHS.CONNECTOR}/connections/authorize`,
    CONNECTION_TEST: (id: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${id}/test`,
    ADMIN: {
      LIST: `${SERVICE_PATHS.CONNECTOR}/connections`,
    },
    FORCE_REFRESH: (id: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${id}/force-refresh`,
    REVOKE: (id: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${id}/revoke`,
    RETRY_AUTHORIZE: (id: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${id}/authorize-url`,
    HISTORY: (id: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${id}/history`,
    TEST_ALL: `${SERVICE_PATHS.CONNECTOR}/connections/test-all`,
    BROWSE: {
      ACC: {
        HUBS: (connId: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${connId}/browse/hubs`,
        PROJECTS: (connId: string, hubId: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${connId}/browse/hubs/${hubId}/projects`,
        TOP_FOLDERS: (connId: string, projectId: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${connId}/browse/projects/${projectId}/top-folders`,
        FOLDER_CONTENTS: (connId: string, projectId: string, folderId: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${connId}/browse/projects/${projectId}/folders/${folderId}/contents`,
      },
      SP: {
        SITES: (connId: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${connId}/sharepoint/browse/sites`,
        LIBRARIES: (connId: string, siteUrl: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${connId}/sharepoint/browse/sites/${encodeURIComponent(siteUrl)}/libraries`,
        CONTENTS: (connId: string, siteUrl: string, libraryId: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${connId}/sharepoint/browse/sites/${encodeURIComponent(siteUrl)}/libraries/${libraryId}/contents`,
      },
      GDRIVE: {
        ROOTS: (connId: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${connId}/browse/googledrive/roots`,
        CONTENTS: (connId: string) => `${SERVICE_PATHS.CONNECTOR}/connections/${connId}/browse/googledrive/contents`,
      },
    },
  },

  // Workflow Service
  WORKFLOW: {
    LIST: `${SERVICE_PATHS.WORKFLOW}/workflows`,
    BY_ID: (id: string) => `${SERVICE_PATHS.WORKFLOW}/workflows/${id}`,
    DELETE: (id: string) => `${SERVICE_PATHS.WORKFLOW}/workflows/${id}`,
    PUBLISH: (id: string) => `${SERVICE_PATHS.WORKFLOW}/workflows/${id}/publish`,
    ARCHIVE: (id: string) => `${SERVICE_PATHS.WORKFLOW}/workflows/${id}/archive`,
    DUPLICATE: (id: string) => `${SERVICE_PATHS.WORKFLOW}/workflows/${id}/duplicate`,
    SAVE_AS_TEMPLATE: (workflowId: string) => `${SERVICE_PATHS.WORKFLOW}/templates/from-workflow/${workflowId}`,
    TRIGGERS: (workflowId: string) => `${SERVICE_PATHS.WORKFLOW}/workflows/${workflowId}/triggers`,
    TEMPLATES: {
      LIST: `${SERVICE_PATHS.WORKFLOW}/templates`,
      BY_ID: (id: string) => `${SERVICE_PATHS.WORKFLOW}/templates/${id}`,
      DELETE: (id: string) => `${SERVICE_PATHS.WORKFLOW}/templates/${id}`,
      CREATE_WORKFLOW: (templateId: string) => `${SERVICE_PATHS.WORKFLOW}/templates/${templateId}/create-workflow`,
    },
  },

  // Orchestration Service
  ORCHESTRATION: {
    EXECUTIONS: `${SERVICE_PATHS.ORCHESTRATION}/executions`,
    EXECUTION_BY_ID: (id: string) => `${SERVICE_PATHS.ORCHESTRATION}/executions/${id}`,
    CANCEL: (id: string) => `${SERVICE_PATHS.ORCHESTRATION}/executions/${id}/cancel`,
    ANALYTICS: {
      EXECUTIONS: `${SERVICE_PATHS.ORCHESTRATION}/analytics/executions`,
      STEPS: `${SERVICE_PATHS.ORCHESTRATION}/analytics/steps`,
    },
    ADMIN: {
      WORKERS: `${SERVICE_PATHS.ORCHESTRATION}/admin/workers`,
      // Cross-tenant DevAdmin-only execution read endpoints (server enforces IsDevAdmin).
      // Returns the same RAW PagedResult / WorkflowExecutionDto shape as the
      // tenant-scoped routes, plus tenantId / workflowName / durationMs.
      EXECUTIONS: `${SERVICE_PATHS.ORCHESTRATION}/admin/executions`,
      EXECUTION_BY_ID: (id: string) => `${SERVICE_PATHS.ORCHESTRATION}/admin/executions/${id}`,
      DEPLOYMENT: {
        DRAIN: `${SERVICE_PATHS.ORCHESTRATION}/admin/deployment/drain`,
        STATUS: `${SERVICE_PATHS.ORCHESTRATION}/admin/deployment/status`,
        RELEASE: `${SERVICE_PATHS.ORCHESTRATION}/admin/deployment/release`,
      },
    },
  },

  // Infrastructure Service (via Gateway proxy: /api/infra/*)
  INFRA: {
    HEALTH_OVERVIEW: '/api/infra/health/overview',
    FK_MAP: '/api/infra/fk-map',
    REDIS: {
      INFO: '/api/infra/redis/info',
      KEYS: '/api/infra/redis/keys',
      KEY_DETAIL: (key: string) => `/api/infra/redis/keys/${encodeURIComponent(key)}`,
    },
    SERVICE_BUS: {
      QUEUES: '/api/infra/servicebus/queues',
      TOPICS: '/api/infra/servicebus/topics',
      QUEUE_DETAIL: (name: string) => `/api/infra/servicebus/queues/${name}`,
    },
  },

  // Audit Service — cross-tenant admin read endpoint
  AUD: {
    ADMIN: {
      EVENTS: `${SERVICE_PATHS.AUDIT}/admin/events`,
    },
  },

  // Database Browser — unified schema-based endpoints (v2)
  DB: {
    SCHEMAS: '/api/infra/db/schemas',
    TABLES: (schema: string) => `/api/infra/db/${schema}/tables`,
    SCHEMA: (schema: string, table: string) => `/api/infra/db/${schema}/tables/${table}/schema`,
    ROWS: (schema: string, table: string) => `/api/infra/db/${schema}/tables/${table}/rows`,
    ROW: (schema: string, table: string, pk: string) => `/api/infra/db/${schema}/tables/${table}/rows/${encodeURIComponent(pk)}`,
    FK_MAP: (schema: string) => `/api/infra/db/${schema}/metadata/fk-map`,
  },

  // Platform Release Manifest
  RELEASE: '/api/v1/release',
} as const
